import React, { Component } from 'react';
import { GoogleApiWrapper } from 'google-maps-react';
import { Row, Empty } from 'antd';
import googleMapsConfig from '../../../../shared/maps/googlemaps';

const styles = {
  mapContainer: {
    position: 'relative',
    width: '100%',
    height: '400px',
  },
  map: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  legend: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '12px 15px',
    borderRadius: 4,
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    zIndex: 1,
    fontSize: 12,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  legendSquare: {
    width: 14,
    height: 14,
    marginRight: 8,
    border: '2px solid',
  },
};

const formatNumber = (num) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const getPolygonColor = (impressions) => {
  if (impressions >= 100000) return { fill: '#1890ff', stroke: '#0050b3' };
  if (impressions >= 70000) return { fill: '#52c41a', stroke: '#237804' };
  if (impressions >= 50000) return { fill: '#faad14', stroke: '#ad6800' };
  return { fill: '#ff4d4f', stroke: '#a8071a' };
};

const extractLocations = (rawCampaignOrders, storeType) => {
  const locations = [];

  (rawCampaignOrders || []).forEach((co) => {
    // Aggregate impressions/clicks for this campaign order
    let totalImpressions = 0;
    let totalClicks = 0;
    (co.aggregateData?.completeAttributionSummary || []).forEach((entry) => {
      totalImpressions += Number(entry.impressions) || 0;
      totalClicks += Number(entry.clicks) || 0;
    });

    (co.complianceAudiences || []).forEach((audience) => {
      (audience.geoTargets || []).forEach((geoTarget) => {
        const location = geoTarget.location;
        if (!location || !location.lat || !location.lng) return;

        // Determine designations from licenses
        const designations = (location.licenses || []).reduce((acc, license) => {
          (license.designations || []).forEach((d) => {
            if (!acc.includes(d)) acc.push(d);
          });
          return acc;
        }, []);

        // Apply store type filter
        if (storeType && storeType !== 'all') {
          const filterValue = storeType.toUpperCase();
          if (!designations.includes(filterValue)) return;
        }

        // Convert GeoJSON polygon coordinates to Google Maps paths
        const polygonPaths = (geoTarget.polygons || []).map((poly) => {
          const coords = poly.geometry?.coordinates;
          if (!coords || !coords[0]) return [];
          // GeoJSON format: [lng, lat] → Google Maps: {lat, lng}
          return coords[0].map(([lng, lat]) => ({ lat, lng }));
        }).filter((path) => path.length > 0);

        // Convert circles
        const circles = (geoTarget.circles || []).map((circle) => ({
          center: { lat: circle.coordinate?.lat, lng: circle.coordinate?.lng },
          radius: circle.radius,
          location: circle.location,
        })).filter((c) => c.center.lat && c.center.lng);

        locations.push({
          name: geoTarget.name || location.name,
          address: location.formattedAddress,
          center: { lat: location.lat, lng: location.lng },
          city: location.city,
          state: location.state,
          designations,
          impressions: totalImpressions,
          clicks: totalClicks,
          polygonPaths,
          circles,
          campaignOrderName: co.name,
        });
      });
    });
  });

  return locations;
};

class PerformanceMapChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      mapReady: false,
    };
    this.polygons = [];
    this.markers = [];
    this.circleOverlays = [];
    this.infoWindows = [];
    this.mapNode = null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.mapNode && !this.map && this.props.google) {
      this.drawMap();
    }

    // Redraw when data or filter changes
    if (
      this.map &&
      (prevProps.rawCampaignOrders !== this.props.rawCampaignOrders ||
        prevProps.storeType !== this.props.storeType)
    ) {
      this.clearOverlays();
      this.drawLocations();
    }
  }

  componentWillUnmount() {
    this.clearOverlays();
  }

  clearOverlays = () => {
    this.polygons.forEach((p) => p.setMap(null));
    this.markers.forEach((m) => m.setMap(null));
    this.circleOverlays.forEach((c) => c.setMap(null));
    this.infoWindows.forEach((iw) => iw.close());
    this.polygons = [];
    this.markers = [];
    this.circleOverlays = [];
    this.infoWindows = [];
  };

  drawMap = () => {
    const { google } = this.props;
    if (!this.mapNode || !google) return;

    this.map = new google.maps.Map(this.mapNode, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeId: 'roadmap',
      mapTypeControl: false,
      scrollwheel: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      streetViewControl: false,
      fullscreenControl: false,
    });

    this.drawLocations();
    this.setState({ loading: false });
  };

  drawLocations = () => {
    const { google, rawCampaignOrders, storeType } = this.props;
    if (!google || !this.map) return;

    const locations = extractLocations(rawCampaignOrders, storeType);
    const bounds = new google.maps.LatLngBounds();

    locations.forEach((loc) => {
      const colors = getPolygonColor(loc.impressions);
      const ctr = loc.impressions > 0 ? ((loc.clicks / loc.impressions) * 100).toFixed(2) : '0.00';

      // Draw polygons
      loc.polygonPaths.forEach((path) => {
        const polygon = new google.maps.Polygon({
          paths: path,
          fillColor: colors.fill,
          fillOpacity: 0.5,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          strokeOpacity: 1,
          map: this.map,
        });

        path.forEach((point) => bounds.extend(point));
        this.polygons.push(polygon);

        const infoWindow = this.createInfoWindow(loc, ctr);
        polygon.addListener('click', () => {
          this.infoWindows.forEach((iw) => iw.close());
          infoWindow.setPosition(loc.center);
          infoWindow.open(this.map);
        });
        this.infoWindows.push(infoWindow);
      });

      // Draw circles
      loc.circles.forEach((circle) => {
        const circleOverlay = new google.maps.Circle({
          center: circle.center,
          radius: circle.radius,
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          map: this.map,
        });

        bounds.extend(circle.center);
        this.circleOverlays.push(circleOverlay);

        const infoWindow = this.createInfoWindow(loc, ctr);
        circleOverlay.addListener('click', () => {
          this.infoWindows.forEach((iw) => iw.close());
          infoWindow.setPosition(circle.center);
          infoWindow.open(this.map);
        });
        this.infoWindows.push(infoWindow);
      });

      // Draw center marker
      const marker = new google.maps.Marker({
        position: loc.center,
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: colors.stroke,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
        title: loc.name,
      });

      bounds.extend(loc.center);
      this.markers.push(marker);

      const markerInfoWindow = this.createInfoWindow(loc, ctr);
      marker.addListener('click', () => {
        this.infoWindows.forEach((iw) => iw.close());
        markerInfoWindow.open(this.map, marker);
      });
      this.infoWindows.push(markerInfoWindow);
    });

    if (locations.length > 0) {
      this.map.fitBounds(bounds);
      if (locations.length === 1) {
        google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
          this.map.setZoom(Math.min(this.map.getZoom(), 15));
        });
      }
    }
  };

  createInfoWindow = (loc, ctr) => {
    const designationTags = loc.designations
      .map((d) => {
        const color = d === 'MEDICAL' ? '#1890ff' : '#52c41a';
        return `<span style="display:inline-block;padding:2px 6px;border-radius:3px;font-size:11px;color:#fff;background:${color};margin-right:4px;">${d}</span>`;
      })
      .join('');

    return new this.props.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 220px;">
          <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px; color: #1890ff;">${loc.name}</div>
          <div style="color: #666; font-size: 12px; margin-bottom: 6px;">${loc.address}</div>
          ${designationTags ? `<div style="margin-bottom: 8px;">${designationTags}</div>` : ''}
          <div style="color: #999; font-size: 11px; margin-bottom: 8px;">${loc.campaignOrderName}</div>
          <div style="border-top: 1px solid #f0f0f0; padding-top: 8px;">
            <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
              <span style="color: #666;">Impressions:</span>
              <span style="font-weight: 500;">${formatNumber(loc.impressions)}</span>
            </div>
            <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
              <span style="color: #666;">Clicks:</span>
              <span style="font-weight: 500;">${formatNumber(loc.clicks)}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #666;">CTR:</span>
              <span style="font-weight: 500;">${ctr}%</span>
            </div>
          </div>
        </div>
      `,
    });
  };

  mapMounted = (node) => {
    if (node && !this.mapNode) {
      this.mapNode = node;
      this.setState({ mapReady: true });
    }
  };

  render() {
    const { loading } = this.state;
    const { rawCampaignOrders, storeType } = this.props;
    const locations = extractLocations(rawCampaignOrders, storeType);
    const hasLocations = locations.length > 0;

    return (
      <div style={styles.mapContainer}>
        {loading && (
          <Row type="flex" style={{ height: '400px', position: 'absolute', width: '100%', zIndex: 0 }} justify="center" align="middle">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Loading map..." />
          </Row>
        )}
        {!loading && !hasLocations && (
          <Row type="flex" style={{ height: '400px' }} justify="center" align="middle">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No geo target data available" />
          </Row>
        )}
        <div ref={this.mapMounted} style={styles.map} />
        {!loading && hasLocations && <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendSquare, backgroundColor: 'rgba(24, 144, 255, 0.5)', borderColor: '#0050b3' }} />
            <span>100K+ impressions</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendSquare, backgroundColor: 'rgba(82, 196, 26, 0.5)', borderColor: '#237804' }} />
            <span>70K - 100K impressions</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendSquare, backgroundColor: 'rgba(250, 173, 20, 0.5)', borderColor: '#ad6800' }} />
            <span>50K - 70K impressions</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendSquare, backgroundColor: 'rgba(255, 77, 79, 0.5)', borderColor: '#a8071a' }} />
            <span>Under 50K impressions</span>
          </div>
        </div>}
      </div>
    );
  }
}

export default GoogleApiWrapper(googleMapsConfig)(PerformanceMapChart);
